import * as moment from 'moment';
import * as _ from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IWatrRequest, IWatrReimbursement } from '@nkr/models/index';
import { Button, Col, ControlLabel, FormControl, FormGroup, Glyphicon, Panel, OverlayTrigger, Alert, Modal, Popover, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { Utility } from '@nkr/services/utility';
import { ReimbursementType } from '@nkr/enums/index';
import { ReimbursementAddEditFormWrapper, ReimbursementWageNotes } from '../styles';
import PopoverTemplate from '@nkr/components/popover-template';
import WeekByWeekBreakdown, { WEEKLY_CAP } from './common/week-by-week-breakdown';
import { isSelfEmployed } from './utils/employment-type.utils';
import { getOtherAmountConsistencyHint, getWageReimbursementSuggested } from './common/wage-reimbursement-calculation';
import { DonorShieldContext } from './context';
import { toast } from 'react-toastify';
import { donorHasComplicationsEvidence, getActiveComplicationsReportFile } from '../utils/watr-complications-helpers';
import { openWatrReceiptInSidebar } from '../utils/open-watr-receipt-in-sidebar';
import { readAdvancedProgressForBuckets, readIsInternationallyEligibleForWatr, resolveDerProgramCap } from './advanced-reimbursement-status-buckets';
import { getArReimbursementVisitCap, getArReimbursementVisitLabel, isIntlArPreOpDisallowed } from './utils/international-ar-visit-caps';
import { formatMoneyDisplay, formatMoneyInput, parseMoneyInput, sanitizeMoneyInputString } from './common/money-input';
import { SsnDisplay } from './ssn-display';

const AMOUNT_DIFFERENCE_EPS = 0.005;

const resolveStandardDerLimit = (request: IWatrRequest | null | undefined): number => {
    if (!request) {
        return 0;
    }

    return resolveDerProgramCap(request);
};

const getCappedTravelDerAmount = (request: IWatrRequest | null | undefined): number => {
    if (!request?.requestTravels?.length) {
        return 0;
    }
    const standardDerLimit = resolveStandardDerLimit(request);
    const approvedTravels = request.requestTravels.filter(x =>
        x.status >= WageAndTravelReimbursementRequestStatus.AutoApproved
        && x.status <= WageAndTravelReimbursementRequestStatus.AdminApproved
    );
    const allowedTotal = approvedTravels.reduce((sum, travel) => sum + (travel.allowedReimbursementAmount || 0), 0);
    return Math.min(Math.max(0, allowedTotal), standardDerLimit);
};

const mergeRequestWeekOther = (request: IWatrRequest, patch: Record<number, number>): IWatrRequest => {
    if (Object.keys(patch).length === 0) {
        return request;
    }
    return {
        ...request,
        requestWeeks: (request.requestWeeks || []).map(w => ({
            ...w,
            otherAmount: patch[w.weekRequested] !== undefined ? patch[w.weekRequested] : w.otherAmount
        }))
    };
};

const popoverWageCalculation = (
    request: IWatrRequest,
    suggested?: ReturnType<typeof getWageReimbursementSuggested> | null
) => {
    const isSE = isSelfEmployed(request.employmentType);
    const ipc = request.incomePercentCovered || 0;
    const title = 'Wage reimbursement calculation';
    let suggestedLine = '';

    if (suggested) {
        if (suggested.noEligibleWeeks) {
            suggestedLine = suggested.allWeeksReimbursed
                ? 'All weeks are already reimbursed.'
                : 'No approved weeks.';
        } else if (suggested.usedSingleWeekFallback) {
            suggestedLine = `${formatMoneyDisplay(suggested.suggestedTotal)}. No week rows on file.`;
        } else {
            suggestedLine = `${formatMoneyDisplay(suggested.suggestedTotal)}. Approved weeks not yet reimbursed.`;
        }
    }

    return (
        <PopoverTemplate style={{ zIndex: 9999 }} id='popover-wage-reimbursement-calc' title={title} dismiss={false}>
            <div style={{ maxWidth: 300, fontSize: 13 }}>
                {suggestedLine ? (
                    <p style={{ marginBottom: 6 }}>{suggestedLine}</p>
                ) : null}
                {suggested && suggested.usedSingleWeekFallback ? (
                    <p style={{ marginBottom: 6 }}>No week rows — using single-week base only.</p>
                ) : null}
                <p style={{ marginBottom: 6 }}>
                    Weekly base: {isSE ? 'tax gross ÷ 52' : 'paystub-derived weekly'}, cap ${WEEKLY_CAP.toLocaleString()}
                    {ipc > 0 ? `, × (100% − ${ipc}%)` : ''}.
                </p>
                <p style={{ marginBottom: 0 }}>
                    Amount is Base × unpaid weeks, minus the sum of Other Amount on those weeks.
                </p>
            </div>
        </PopoverTemplate>
    );
};

interface IValidation {
    type: boolean,
    amount: boolean,
    confirmationCode: boolean,
    date: boolean,
    closeCard?: boolean
}

const initialValidation: IValidation = {
    type: true,
    amount: true,
    confirmationCode: true,
    date: true
};

interface IProps {
    data: IWatrReimbursement,
    isSuperUser: boolean,
    request?: IWatrRequest,
    reimbursements: IWatrReimbursement[],
    typeList: any[],
    isReimburseTravel: boolean,
    isReimburseWage: boolean,
    defaultType?: number,
    entryMode?: 'standard' | 'reconcileAdvanced' | 'intlArVisit',
    onNewEntry: (type: number, amount: number, confirmationCode: string, date: string, closeAdvancedReimbursementCard?: boolean) => void,
    onUpdate: (reimbId: number, type: number, amount: number, confirmationCode: string, date: string) => void,
    onCancel: () => void
}

interface IPendingOtherAmount {
    weekRequested: number;
    nextValue: number;
    previousValue: number;
}

const ReimbursementEntry = (props: IProps) => {
    const shieldContext = useContext(DonorShieldContext);
    const [type, setType] = useState(0);
    const [amount, setAmount] = useState('');
    const [amountFocused, setAmountFocused] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [date, setDate] = useState('');
    const [validation, setValidation] = useState<IValidation>(initialValidation);
    const [dirty, setDirty] = useState(false);
    const [localOtherPatch, setLocalOtherPatch] = useState<Record<number, number>>({});
    const [otherAmountDraft, setOtherAmountDraft] = useState<Record<number, number>>({});
    const [pendingOtherAmount, setPendingOtherAmount] = useState<IPendingOtherAmount | null>(null);
    const [savingOtherWeek, setSavingOtherWeek] = useState<number | null>(null);
    const [saveOtherError, setSaveOtherError] = useState<string | null>(null);
    const [otherAmountConfirmInitials, setOtherAmountConfirmInitials] = useState('');
    const [closeAdvancedReimbursementCard, setCloseAdvancedReimbursementCard] = useState<boolean | null>(null);
    const [visitCapError, setVisitCapError] = useState<string | null>(null);
    const prefillKeyRef = useRef('');

    useEffect(() => {
        if (props.data != null) {
            const editKey = `edit:${props.data.id}`;
            if (prefillKeyRef.current === editKey) {
                return;
            }
            prefillKeyRef.current = editKey;
            calculateAmount();
            return;
        }

        if (!props.request) {
            prefillKeyRef.current = 'none';
            calculateAmount();
            return;
        }

        const prefillKey = `${props.entryMode || 'standard'}:${props.request.id}:new`;
        if (prefillKeyRef.current === prefillKey) {
            return;
        }
        prefillKeyRef.current = prefillKey;
        calculateAmount();
    }, [props.entryMode, props.request?.id, props.data?.id]);

    useEffect(() => {
        if (props.entryMode === 'reconcileAdvanced') {
            setCloseAdvancedReimbursementCard(null);
        }
    }, [props.entryMode, props.request?.id]);

    useEffect(() => {
        setLocalOtherPatch({});
    }, [props.request?.id]);

    useEffect(() => {
        if (pendingOtherAmount != null) {
            setOtherAmountConfirmInitials('');
        }
    }, [pendingOtherAmount]);

    const requestForCalc = useMemo(
        () => (props.request ? mergeRequestWeekOther(props.request, localOtherPatch) : null),
        [props.request, localOtherPatch]
    );

    const wageSuggested = useMemo(() => {
        if (!requestForCalc || type !== ReimbursementType.Wages) {
            return null;
        }
        return getWageReimbursementSuggested(requestForCalc);
    }, [requestForCalc, type]);

    const otherAmountConsistency = useMemo(() => {
        if (!requestForCalc || type !== ReimbursementType.Wages) {
            return null;
        }
        return getOtherAmountConsistencyHint(requestForCalc);
    }, [requestForCalc, type]);

    const consistencyAfterPendingSave = useMemo(() => {
        if (!pendingOtherAmount || !props.request) {
            return null;
        }
        const merged = mergeRequestWeekOther(props.request, {
            ...localOtherPatch,
            [pendingOtherAmount.weekRequested]: pendingOtherAmount.nextValue
        });
        return getOtherAmountConsistencyHint(merged);
    }, [pendingOtherAmount, props.request, localOtherPatch]);

    const canEditOtherInForm = props.isSuperUser && !!shieldContext?.onUpdateRequestWeekOtherAmount;

    const pendingOtherWeeks = useMemo(() => {
        const weeks = requestForCalc?.requestWeeks || [];

        return Object.keys(otherAmountDraft)
            .map(k => Number(k))
            .filter(week => {
                const saved = weeks.find(w => w.weekRequested === week)?.otherAmount ?? 0;

                return Math.abs((otherAmountDraft[week] ?? 0) - saved) >= AMOUNT_DIFFERENCE_EPS;
            })
            .sort((a, b) => a - b);
    }, [otherAmountDraft, requestForCalc]);

    const hasPendingOtherAmount = pendingOtherWeeks.length > 0;

    const priorWageReimbursementRows = useMemo(() =>
        props.reimbursements.filter(r =>
            r?.reimbursementType === ReimbursementType.Wages
            || r?.reimbursementType === ReimbursementType.WagesForComplication
            || r?.reimbursementType === ReimbursementType.CreditReturnLostWageReimbursement),
        [props.reimbursements]);

    const priorWageReimbursementsTotal = useMemo(() =>
        _.sumBy(priorWageReimbursementRows, r => (r.amount != null ? r.amount : 0)),
        [priorWageReimbursementRows]);

    const priorWageAchEntryCount = priorWageReimbursementRows.length;
    const hasPriorWagePayments = priorWageReimbursementsTotal > 0 || priorWageAchEntryCount > 0;

    const wagePaymentFormula = useMemo(() => {
        if (!wageSuggested || wageSuggested.noEligibleWeeks) {
            return null;
        }

        const weekCount = wageSuggested.usedSingleWeekFallback ? 1 : wageSuggested.rows.length;
        const otherSum = wageSuggested.rows.reduce((sum, row) => sum + (row.otherAmount || 0), 0);
        const weekSubtotal = wageSuggested.adjustedBaseAmount * weekCount;

        return {
            weekAmt: formatMoneyDisplay(weekSubtotal),
            weekDesc: weekCount === 1
                ? '(1 unpaid week)'
                : `(${formatMoneyDisplay(wageSuggested.adjustedBaseAmount)} × ${weekCount} unpaid weeks)`,
            otherAmt: otherSum >= AMOUNT_DIFFERENCE_EPS
                ? `− ${formatMoneyDisplay(otherSum)}`
                : null,
            otherDesc: weekCount > 1 ? '(sum of Other Amount)' : '(Other Amount)'
        };
    }, [wageSuggested]);

    const showWageSideNotes = !!wageSuggested && (
        (otherAmountConsistency?.weeksBreakingPriorZeroPattern.length ?? 0) > 0
        || wageSuggested.noEligibleWeeks
    );

    const complicationsReportFile = useMemo(
        () => getActiveComplicationsReportFile(props.request),
        [props.request?.fileUploadRecords]
    );

    const showComplicationsInForm = useMemo(
        () => donorHasComplicationsEvidence(props.request, props.reimbursements),
        [props.request, props.reimbursements]
    );

    const requestOtherAmountSave = (weekRequested: number, otherAmount: number) => {
        const w = (props.request?.requestWeeks || []).find(x => x.weekRequested === weekRequested);
        const previousValue = w?.otherAmount ?? 0;
        if (Math.abs(previousValue - otherAmount) < 0.005) {
            toast.info('No change to save.');
            return;
        }
        setSaveOtherError(null);
        setPendingOtherAmount({ weekRequested, nextValue: otherAmount, previousValue });
    };

    const handleConfirmOtherAmount = async () => {
        if (!pendingOtherAmount || !props.request || !shieldContext?.onUpdateRequestWeekOtherAmount) {
            return;
        }
        const initialsTrimmed = otherAmountConfirmInitials.trim();
        if (!initialsTrimmed) {
            setSaveOtherError('Please enter your initials to confirm.');
            return;
        }
        const { weekRequested, nextValue, previousValue } = pendingOtherAmount;
        setSavingOtherWeek(weekRequested);
        setSaveOtherError(null);
        try {
            const response = await shieldContext.onUpdateRequestWeekOtherAmount(
                props.request.id,
                weekRequested,
                nextValue,
                initialsTrimmed
            );
            const res = response as any;
            if (res?.data?.success) {
                const nextPatch = { ...localOtherPatch, [weekRequested]: nextValue };
                setLocalOtherPatch(nextPatch);
                setOtherAmountDraft(prev => {
                    const next = { ...prev };
                    delete next[weekRequested];
                    return next;
                });
                const merged = mergeRequestWeekOther(props.request, nextPatch);
                setAmount(getWageReimbursementSuggested(merged).suggestedTotal.toFixed(2));
                setPendingOtherAmount(null);
            } else {
                const err = res?.data?.message || 'Save failed.';
                setSaveOtherError(err);
                toast.error(err);
            }
        } catch (e: any) {
            const errMsg = e?.response?.data?.message || e?.message || 'Save failed.';
            setSaveOtherError(errMsg);
            toast.error(errMsg);
        } finally {
            setSavingOtherWeek(null);
        }
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'type') {
            const nextType = parseInt(value);
            setType(nextType);
            if (props.entryMode !== 'intlArVisit' && props.entryMode !== 'reconcileAdvanced') {
                updateAmount(nextType);
            }
            setVisitCapError(null);
        }

        if (name == 'amount') {
            setAmount(sanitizeMoneyInputString(value));
            setVisitCapError(null);
        }

        if (name == 'confirmationCode') {
            setConfirmationCode(value);
        }

        if (name == 'date') {
            setDate(value);
        }
    };

    const handleSave = () => {
        if (hasPendingOtherAmount) {
            toast.error('Save Other Amount before recording reimbursement.');
            return;
        }

        const isReconcileAdvanced = props.entryMode === 'reconcileAdvanced';
        let updatedValidation: IValidation = {
            type: type > 0,
            amount: Utility.isNumber(amount),
            confirmationCode: confirmationCode.length > 0,
            date: moment(date).isValid(),
            closeCard: !isReconcileAdvanced || closeAdvancedReimbursementCard !== null
        };

        let isValid = Object.values(updatedValidation).indexOf(false) < 0;

        setValidation(updatedValidation);
        setDirty(true);
        setVisitCapError(null);

        if (isValid) {
            let savedAmount = Math.round(parseMoneyInput(amount) * 100) / 100;
            if (type === ReimbursementType.CreditReturnLostWageReimbursement) {
                savedAmount = -Math.abs(savedAmount);
            }
            if (isIntlArPreOpDisallowed(type, props.request)) {
                const msg = 'Pre-Op Advanced Reimbursement is not available for international donors.';
                setVisitCapError(msg);
                toast.error(msg);
                return;
            }

            const visitCap = getArReimbursementVisitCap(type, props.request);
            if (visitCap != null && savedAmount > visitCap) {
                const label = getArReimbursementVisitLabel(type);
                const msg = `${label} Advanced Reimbursement exceeds the $${visitCap.toLocaleString('en-US')} visit cap.`;
                setVisitCapError(msg);
                toast.error(msg);
                return;
            }

            if (props.data != null && props.data.id > 0) {
                props.onUpdate(props.data.id, type, savedAmount, confirmationCode, date);
            } else {
                props.onNewEntry(
                    type,
                    savedAmount,
                    confirmationCode,
                    date,
                    isReconcileAdvanced ? closeAdvancedReimbursementCard === true : undefined
                );
            }
        }
    };

    const updateAmount = (nextType: number) => {
        let nextAmount = 0;

        if (nextType == ReimbursementType.TravelAndLodging && props.request != null) {
            nextAmount = getCappedTravelDerAmount(props.request);
        } else if (nextType == ReimbursementType.Wages && props.request != null) {
            const merged = mergeRequestWeekOther(props.request, localOtherPatch);
            nextAmount = getWageReimbursementSuggested(merged).suggestedTotal;
        }

        setAmount(nextAmount.toFixed(2));
    };

    const calculateAmount = () => {
        if (props.data != null) {
            let { date, reimbursementType, amount, confirmationCode } = props.data;
            let newDateValue = moment(date, 'MM/DD/YYYY').format('YYYY-MM-DD');

            setType(reimbursementType);
            const loadedAmount = reimbursementType === ReimbursementType.CreditReturnLostWageReimbursement
                ? -Math.abs(amount)
                : amount;
            setAmount(loadedAmount.toFixed(2).toString());
            setDate(newDateValue);
            setConfirmationCode(confirmationCode);
            return;
        }

        let { isReimburseTravel, isReimburseWage, defaultType } = props;
        const req = props.request;
        if (!req) {
            setAmount('0.00');
            setType(0);
            setDate(moment().format('YYYY-MM-DD'));
            return;
        }

        if (props.entryMode === 'intlArVisit') {
            const travels = req.requestTravels || [];
            const approvedTravels = travels.filter(x =>
                x.status >= WageAndTravelReimbursementRequestStatus.AutoApproved
                && x.status <= WageAndTravelReimbursementRequestStatus.AdminApproved
            );
            let amt = approvedTravels.reduce((sum, travel) =>
                sum + (travel.allowedReimbursementAmount
                    || travel.travelReimbursementTotal
                    || travel.travelReceiptsTotal
                    || 0), 0);
            if (!amt || amt <= 0) {
                amt = req.tlr || 0;
            }
            if (!amt || amt <= 0) {
                const chaseAmt = req.pendingTransactionAmount ?? req.transactionAmount;
                amt = typeof chaseAmt === 'number' && !isNaN(chaseAmt) ? chaseAmt : 0;
            }
            const visitCap = getArReimbursementVisitCap(ReimbursementType.InternationalWorkup, req);
            if (visitCap != null && amt > visitCap) {
                amt = visitCap;
            }
            setType(ReimbursementType.InternationalWorkup);
            setAmount(amt.toFixed(2));
            setConfirmationCode(req.pendingTransactionConfirmation || '');
            setDate(moment().format('YYYY-MM-DD'));
            return;
        }

        if (props.entryMode === 'reconcileAdvanced') {
            const amt = req.pendingTransactionAmount ?? req.transactionAmount;
            const chaseSendRaw = req.pendingTransactionSendOn;
            const progress = readAdvancedProgressForBuckets(req);
            const preOpDate = (progress?.preOpScheduledDate || '').trim();
            const hasPreOpScheduled = Number(progress?.networkRemoteStatus) === 25
                || preOpDate !== '';
            const intlEligible = readIsInternationallyEligibleForWatr(req);
            setType(
                hasPreOpScheduled && !intlEligible
                    ? ReimbursementType.AdvancedReimbursementPreOp
                    : ReimbursementType.AdvancedReimbursement
            );
            setAmount(typeof amt === 'number' && !isNaN(amt) ? amt.toFixed(2) : '0.00');
            setConfirmationCode(req.pendingTransactionConfirmation || '');
            setDate(
                chaseSendRaw && moment(chaseSendRaw).isValid()
                    ? moment(chaseSendRaw).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD')
            );
            return;
        }

        let reimbursementAmt = 0;
        let reimbursementType = 0;

        if (defaultType === ReimbursementType.TravelAndLodging) {
            isReimburseTravel = true;
            isReimburseWage = false;
        } else if (defaultType === ReimbursementType.Wages) {
            isReimburseTravel = false;
            isReimburseWage = true;
        }

        if (isReimburseTravel == true) {
            reimbursementAmt = getCappedTravelDerAmount(req);
            reimbursementType = ReimbursementType.TravelAndLodging;
        } else if (isReimburseWage) {
            const wageResult = getWageReimbursementSuggested(req);
            reimbursementAmt = wageResult.suggestedTotal;
            reimbursementType = ReimbursementType.Wages;
        } else {
            reimbursementAmt = (req.actualWageReimbursementAmount || 0) * 2;
        }

        setAmount(reimbursementAmt.toFixed(2).toString());
        setType(reimbursementType);
        setDate(moment().format('YYYY-MM-DD'));
    };

    let { data, typeList, request, isSuperUser } = props;

    const isReconcileAdvanced = props.entryMode === 'reconcileAdvanced';
    const isIntlArVisit = props.entryMode === 'intlArVisit';
    const isArVisitEntry = isReconcileAdvanced || isIntlArVisit;
    const isIntlEligible = props.request ? readIsInternationallyEligibleForWatr(props.request) : false;
    const reconcileAdvancedProgress = isArVisitEntry && props.request
        ? readAdvancedProgressForBuckets(props.request)
        : undefined;
    const reconcilePreOpDate = (reconcileAdvancedProgress?.preOpScheduledDate || '').trim();
    const reconcileHasPreOpScheduled = Number(reconcileAdvancedProgress?.networkRemoteStatus) === 25
        || reconcilePreOpDate !== '';
    const arVisitTypeIds = isIntlArVisit
        ? [ReimbursementType.InternationalWorkup]
        : isIntlEligible
        ? [ReimbursementType.AdvancedReimbursement]
        : [
            ReimbursementType.AdvancedReimbursement,
            ReimbursementType.AdvancedReimbursementPreOp
        ];
    let reimbursementTypes = isArVisitEntry
        ? (typeList || []).filter(item => arVisitTypeIds.includes(item.id))
        : typeList;
    if (isIntlArVisit
        && (!reimbursementTypes || reimbursementTypes.length === 0
            || !reimbursementTypes.some(item => item.id === ReimbursementType.InternationalWorkup))) {
        reimbursementTypes = [
            { id: ReimbursementType.InternationalWorkup, description: 'International Workup' }
        ];
    }
    const panelTitle = isReconcileAdvanced
        ? 'Reconcile Transactions'
        : (props.data == null ? 'New Reimbursement Payment' : 'Edit Reimbursement Payment');
    const activeVisitCap = isArVisitEntry
        ? getArReimbursementVisitCap(type, props.request)
        : null;

    let hasDonorId = (request.donorId > 0 || request.donorShieldId > 0);
    let showAddReimbursement = hasDonorId && isSuperUser;

    let weeks = request.requestWeeks.length > 0
        ? request.requestWeeks.filter(x => x.status > WageAndTravelReimbursementRequestStatus.PendingApproval && x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined).map(i => i.weekRequested)
        : [];

    let travels = request.requestTravels.length > 0
        ? request
            .requestTravels
            .filter(x => x.status > WageAndTravelReimbursementRequestStatus.PendingApproval && x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined)
            .map(x => Utility.getOrdinalSuffixOfDate(x.createdDateTime))
        : [];

    let weeksRequested = data != null ? data.weeksRequested : weeks.join(', ');

    let travelsRequested = data != null ? data.travelsRequested : travels.join(', ');

    let prevReimbursed = request.requestTravels?.find(travel => travel.reimbursementAmount !== undefined);
    let allowedReimbursementAmount = getCappedTravelDerAmount(props.request);

    let wageType = type == ReimbursementType.Wages;
    let creditReturnWageType = type == ReimbursementType.CreditReturnLostWageReimbursement;
    let travelType = type == ReimbursementType.TravelAndLodging;
    let buttonLabel = data != null ? 'Update Reimbursement' : 'Record Reimbursement';

    if (data == null && isArVisitEntry && closeAdvancedReimbursementCard === true) {
        buttonLabel = 'Save and close card';
    }

    let isValid = Object.values(validation).indexOf(false) < 0;

    const suggestedNumeric = wageSuggested?.suggestedTotal ?? null;
    const enteredNumeric = Utility.isNumber(amount) ? parseFloat(amount) : null;
    const showAmountDeviationWarning = suggestedNumeric != null && enteredNumeric != null
        && Math.abs(enteredNumeric - suggestedNumeric) > AMOUNT_DIFFERENCE_EPS;

    const wageContextId = 'wage-reimbursement-suggested-summary';
    const otherAmountInitialsTrimmed = otherAmountConfirmInitials.trim();
    const otherAmountInitialsOk = otherAmountInitialsTrimmed.length > 0 && otherAmountInitialsTrimmed.length <= 3;

    return (
        <>
        <style>{`
            .watr-other-amount-confirm-modal.modal.in {
                display: flex !important;
                align-items: center;
                justify-content: center;
            }
            .watr-other-amount-confirm-modal .modal-dialog {
                margin: 0 auto;
            }
        `}</style>
        <Panel bsStyle='primary'>
            <Panel.Heading style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                {showAddReimbursement &&
                    <Panel.Title>{panelTitle}</Panel.Title>
                }

                {!hasDonorId && (
                    <Alert bsStyle='danger'>This request does not have a donor bound to it.  Please contact Ops.</Alert>
                )}
            </Panel.Heading>
            <Panel.Body>
                {!isValid && dirty && (
                    <Alert bsStyle='danger' style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                        Unable to create reimbursement.  The  fields in red are required.
                    </Alert>
                )}

                {visitCapError && (
                    <Alert
                        bsStyle='danger'
                        data-testid='ds-admin-ar-visit-cap-error'
                        style={{ paddingTop: 7.5, paddingBottom: 7.5 }}
                    >
                        {visitCapError}
                    </Alert>
                )}

                {showAmountDeviationWarning && (
                    <Alert bsStyle='warning' style={{ paddingTop: 10, paddingBottom: 10, fontSize: 14 }}>
                        Amount is different from the suggested total — confirm if intentional.
                    </Alert>
                )}
                {creditReturnWageType && Utility.isNumber(amount) && parseFloat(amount) >= 0 && (
                    <Alert bsStyle='warning' style={{ paddingTop: 10, paddingBottom: 10, fontSize: 14 }}>
                        Credit Return should usually be entered as a negative amount.
                    </Alert>
                )}

                {props.isReimburseWage && showComplicationsInForm && props.request && (
                    <Alert bsStyle='info' style={{ paddingTop: 10, paddingBottom: 10, fontSize: 14, marginBottom: 10 }}>
                        <strong>Complications:</strong>{' '}
                        Yes — this donor has complication-related activity on this request.
                        {shieldContext && complicationsReportFile ? (
                            <>
                                {' '}
                                <Button
                                    bsStyle='link'
                                    bsSize='xsmall'
                                    style={{ padding: 0, verticalAlign: 'baseline' }}
                                    onClick={() =>
                                        openWatrReceiptInSidebar(
                                            shieldContext.downloadFile,
                                            shieldContext.onShowDataSideBar,
                                            complicationsReportFile.id
                                        )
                                    }
                                >
                                    View complications report
                                </Button>
                            </>
                        ) : props.request.hasActiveComplicationsReport ? (
                            <span style={{ color: '#555' }}>
                                {' '}
                                A complications report is flagged on file. If the link is missing, refresh the request or open <strong>Uploaded documents</strong> in the accordion.
                            </span>
                        ) : (
                            <span style={{ color: '#555' }}>
                                {' '}
                                No complications report PDF is on file yet.
                            </span>
                        )}
                    </Alert>
                )}

                <ReimbursementAddEditFormWrapper horizontal onSubmit={(e: React.FormEvent) => e.preventDefault()}>
                    <FormGroup validationState={dirty && validation.type == false ? 'error' : null}>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Type:</Col>
                        <Col xs={12} lg={8}>
                            <FormControl
                                componentClass='select'
                                name='type'
                                value={type}
                                onChange={handleInputChange}
                            >
                                <option value={0}>- Select Type -</option>
                                {reimbursementTypes.map(item => {
                                    return <option key={item.id} value={item.id}>{item.description}</option>;
                                })}
                            </FormControl>
                        </Col>
                    </FormGroup>

                    {props.isReimburseWage && wageType && wageSuggested ? (
                        <>
                            {weeksRequested != '' && (
                                <FormGroup>
                                    <Col xs={12} lg={4} componentClass={ControlLabel}>Weeks:</Col>
                                    <Col xs={12} lg={8}>
                                        <div className='form-control-static'>{weeksRequested}</div>
                                    </Col>
                                </FormGroup>
                            )}

                            {requestForCalc && (
                                <FormGroup>
                                    <Col xs={12}>
                                        <div style={{ fontSize: 15 }}>
                                            <WeekByWeekBreakdown
                                                data={requestForCalc}
                                                allowEditOtherAmount={canEditOtherInForm}
                                                draftOtherAmounts={otherAmountDraft}
                                                onOtherAmountChange={(weekRequested, otherAmount) => {
                                                    setOtherAmountDraft(prev => ({ ...prev, [weekRequested]: otherAmount }));
                                                }}
                                                onSaveOtherAmount={canEditOtherInForm ? requestOtherAmountSave : undefined}
                                                savingWeekRequested={savingOtherWeek}
                                                saveError={saveOtherError}
                                                wellStyle={{ marginTop: 0 }}
                                                briefHelp={true}
                                                showApproverColumns={true}
                                            />
                                        </div>
                                    </Col>
                                </FormGroup>
                            )}

                            <Row>
                                <Col xs={12} md={showWageSideNotes ? 7 : 12}>
                                    {hasPriorWagePayments && (
                                        <FormGroup>
                                            <Col xs={12} sm={4} componentClass={ControlLabel}>Reimbursed previously:</Col>
                                            <Col xs={12} sm={8}>
                                                <div className='form-control-static'>
                                                    {formatMoneyDisplay(priorWageReimbursementsTotal)}
                                                </div>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    {wagePaymentFormula && (
                                        <FormGroup>
                                            <Col xs={12} sm={4} componentClass={ControlLabel}>Formula:</Col>
                                            <Col xs={12} sm={8}>
                                                <div className='wage-formula' id={wageContextId}>
                                                    <span className='wage-formula-line'>
                                                        <span className='wage-formula-amt'>{wagePaymentFormula.weekAmt}</span>
                                                        <span className='wage-formula-desc'>{wagePaymentFormula.weekDesc}</span>
                                                    </span>
                                                    {wagePaymentFormula.otherAmt && (
                                                        <span className='wage-formula-line'>
                                                            <span className='wage-formula-amt'>{wagePaymentFormula.otherAmt}</span>
                                                            <span className='wage-formula-desc'>{wagePaymentFormula.otherDesc}</span>
                                                        </span>
                                                    )}
                                                    {wageSuggested.usedSingleWeekFallback && (
                                                        <span className='wage-formula-note'>No week rows on file. Using one week of Base.</span>
                                                    )}
                                                </div>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup validationState={dirty && validation.amount == false ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>
                                            <OverlayTrigger trigger={['hover', 'focus']} overlay={popoverWageCalculation(props.request, wageSuggested)}>
                                                <Glyphicon glyph='info-sign' style={{ cursor: 'help' }} aria-label='Wage reimbursement calculation help' />
                                            </OverlayTrigger>
                                            &nbsp;Amount:
                                        </Col>
                                        <Col xs={12} sm={8}>
                                            <FormControl
                                                type='text'
                                                inputMode='decimal'
                                                name='amount'
                                                value={amountFocused
                                                    ? amount
                                                    : (Utility.isNumber(amount) ? formatMoneyInput(parseMoneyInput(amount)) : amount)}
                                                onFocus={() => setAmountFocused(true)}
                                                onBlur={() => {
                                                    setAmountFocused(false);
                                                    if (Utility.isNumber(amount)) {
                                                        setAmount(parseMoneyInput(amount).toFixed(2));
                                                    }
                                                }}
                                                onChange={handleInputChange}
                                                aria-describedby={wagePaymentFormula ? wageContextId : undefined}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={dirty && validation.confirmationCode == false ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Confirmation Code:</Col>
                                        <Col xs={12} sm={8}>
                                            <FormControl
                                                type='text'
                                                name='confirmationCode'
                                                value={confirmationCode}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={dirty && validation.date == false ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Date:</Col>
                                        <Col xs={12} sm={8}>
                                            <FormControl
                                                type='date'
                                                name='date'
                                                value={date}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </FormGroup>
                                </Col>
                                {showWageSideNotes && (
                                    <Col xs={12} md={5}>
                                        <ReimbursementWageNotes>
                                            {otherAmountConsistency && otherAmountConsistency.weeksBreakingPriorZeroPattern.length > 0 && (
                                                <div className='wage-note'>
                                                    <Alert bsStyle='warning' style={{ fontSize: 14 }}>
                                                        <strong>Check Other Amount. </strong>
                                                        Week{otherAmountConsistency.weeksBreakingPriorZeroPattern.length > 1 ? 's' : ''}{' '}
                                                        {otherAmountConsistency.weeksBreakingPriorZeroPattern.join(', ')}
                                                        {' '}{otherAmountConsistency.weeksBreakingPriorZeroPattern.length > 1 ? 'have' : 'has'} an offset; earlier weeks are $0. Confirm this is other income, not wage in the wrong column.
                                                    </Alert>
                                                </div>
                                            )}
                                            {wageSuggested.noEligibleWeeks && (
                                                <div className='wage-note'>
                                                    <span className='wage-note-title'>This payment</span>
                                                    <p>
                                                        {wageSuggested.allWeeksReimbursed
                                                            ? 'All weeks are already reimbursed.'
                                                            : 'No approved weeks.'}
                                                    </p>
                                                </div>
                                            )}
                                        </ReimbursementWageNotes>
                                    </Col>
                                )}
                            </Row>
                        </>
                    ) : (
                        <>
                            {weeksRequested != '' && wageType &&
                                <FormGroup>
                                    <Col xs={12} lg={4} componentClass={ControlLabel}>Weeks:</Col>
                                    <Col xs={12} lg={8}>
                                        <div className='form-control-static'>
                                            {weeksRequested}
                                        </div>
                                    </Col>
                                </FormGroup>
                            }

                            {travelsRequested != '' && travelType &&
                                <>
                                    <FormGroup>
                                        <Col xs={12} lg={4} componentClass={ControlLabel}>Travel Requests:</Col>
                                        <Col xs={12} lg={8}>
                                            <div className='form-control-static'>
                                                {travelsRequested}
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup>
                                        <Col xs={12} lg={4} componentClass={ControlLabel}>Reimbursed Previously:</Col>
                                        <Col xs={12} lg={8}>
                                            <div className='form-control-static'>
                                                {formatMoneyDisplay(prevReimbursed?.reimbursementAmount < 0 ? 0 : (prevReimbursed?.reimbursementAmount || 0))}
                                            </div>
                                        </Col>
                                    </FormGroup>
                                    <FormGroup>
                                        <Col xs={12} lg={4} componentClass={ControlLabel}>Amount to Reimburse:</Col>
                                        <Col xs={12} lg={8}>
                                            <div className='form-control-static'>
                                                {formatMoneyDisplay(allowedReimbursementAmount)}
                                            </div>
                                        </Col>
                                    </FormGroup>
                                </>
                            }

                            <FormGroup validationState={dirty && validation.amount == false ? 'error' : null}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>
                                    {props.isReimburseWage &&
                                        <OverlayTrigger trigger={['hover', 'focus']} overlay={popoverWageCalculation(props.request, wageSuggested)}>
                                            <Glyphicon glyph='info-sign' style={{ cursor: 'help' }} aria-label='Wage reimbursement calculation help' />
                                        </OverlayTrigger>
                                    }&nbsp;Amount:
                                </Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        type='text'
                                        inputMode='decimal'
                                        name='amount'
                                        value={amountFocused
                                            ? amount
                                            : (Utility.isNumber(amount) ? formatMoneyInput(parseMoneyInput(amount)) : amount)}
                                        onFocus={() => setAmountFocused(true)}
                                        onBlur={() => {
                                            setAmountFocused(false);
                                            if (Utility.isNumber(amount)) {
                                                setAmount(parseMoneyInput(amount).toFixed(2));
                                            }
                                        }}
                                        onChange={handleInputChange}
                                        aria-describedby={props.isReimburseWage && wageType ? wageContextId : undefined}
                                    />
                                    {props.isReimburseWage && wageType && (
                                        <div className='help-block' style={{ fontSize: 14, color: '#5a6268' }}>
                                            Override allowed (partial pay / corrections).
                                        </div>
                                    )}
                                    {creditReturnWageType && (
                                        <div className='help-block' style={{ fontSize: 14, color: '#5a6268' }}>
                                            Credit Return adjusts prior Lost Wage reimbursement.
                                        </div>
                                    )}
                                    {activeVisitCap != null && type > 0 && (
                                        <div className='help-block' style={{ fontSize: 14, color: '#5a6268' }}>
                                            {getArReimbursementVisitLabel(type)} visit cap: {formatMoneyDisplay(activeVisitCap)}.
                                        </div>
                                    )}
                                </Col>
                            </FormGroup>

                            <FormGroup validationState={dirty && validation.confirmationCode == false ? 'error' : null}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Confirmation Code:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        type='text'
                                        name='confirmationCode'
                                        value={confirmationCode}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup validationState={dirty && validation.date == false ? 'error' : null}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Date:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        type='date'
                                        name='date'
                                        value={date}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </FormGroup>
                        </>
                    )}

                    {isReconcileAdvanced && (
                        <FormGroup validationState={dirty && validation.closeCard === false ? 'error' : null}>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>Close card account?</Col>
                            <Col xs={12} lg={8}>
                                {reconcileHasPreOpScheduled && (
                                    <Alert bsStyle='warning' style={{ fontSize: 13, marginBottom: 10, padding: '8px 10px' }}>
                                        Pre-op is scheduled{reconcilePreOpDate ? ` (${reconcilePreOpDate})` : ''}. Confirm workup reconciliation is complete and whether pre-op funding/reload is needed before choosing <strong>Yes</strong>.
                                    </Alert>
                                )}
                                <ToggleButtonGroup
                                    type='radio'
                                    name='closeAdvancedReimbursementCard'
                                    value={closeAdvancedReimbursementCard === true ? 1 : closeAdvancedReimbursementCard === false ? 2 : undefined}
                                >
                                    <ToggleButton
                                        value={1}
                                        data-testid='ds-admin-ar-close-card-yes'
                                        className={closeAdvancedReimbursementCard === true ? 'btn-primary btn-sm' : 'btn-sm'}
                                        onClick={() => setCloseAdvancedReimbursementCard(true)}
                                    >
                                        Yes
                                    </ToggleButton>
                                    <ToggleButton
                                        value={2}
                                        data-testid='ds-admin-ar-close-card-no'
                                        className={closeAdvancedReimbursementCard === false ? 'btn-primary btn-sm' : 'btn-sm'}
                                        onClick={() => setCloseAdvancedReimbursementCard(false)}
                                    >
                                        No
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <div className='help-block' style={{ fontSize: 14, color: '#5a6268', marginBottom: 0 }}>
                                    <strong>Yes</strong> saves reconciliation and moves the request to <strong>Closed</strong>.
                                    {' '}<strong>No</strong> saves reimbursement only; the card stays on the <strong>Active Card</strong> list.
                                </div>
                                {dirty && validation.closeCard === false && (
                                    <div className='text-danger' style={{ fontSize: 13, marginTop: 6 }}>Select Yes or No.</div>
                                )}
                            </Col>
                        </FormGroup>
                    )}
                </ReimbursementAddEditFormWrapper>

                <Modal
                    className='watr-other-amount-confirm-modal'
                    show={pendingOtherAmount != null}
                    onHide={() => !savingOtherWeek && setPendingOtherAmount(null)}
                    backdrop={savingOtherWeek ? 'static' : true}
                    keyboard={!savingOtherWeek}
                >
                    <Modal.Header closeButton={!savingOtherWeek}>
                        <Modal.Title>Confirm Other Amount</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ fontSize: 15, paddingBottom: 12 }}>
                        {pendingOtherAmount && (
                            <>
                                <p style={{ marginBottom: 12 }}>
                                    Week <strong>{pendingOtherAmount.weekRequested}</strong>: update Other Amount to{' '}
                                    <strong>{pendingOtherAmount.nextValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</strong>
                                    {' '}(was{' '}
                                    {pendingOtherAmount.previousValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})?
                                </p>
                                {consistencyAfterPendingSave
                                    && consistencyAfterPendingSave.weeksBreakingPriorZeroPattern.includes(pendingOtherAmount.weekRequested)
                                    && pendingOtherAmount.nextValue > 0.005 && (
                                    <Alert bsStyle='warning' style={{ fontSize: 14, marginBottom: 12 }}>
                                        After this save, week {pendingOtherAmount.weekRequested} will have Other Amount while every earlier approved week stayed at $0.
                                        Please confirm this is <strong>not</strong> the weekly wage—only a true offset (other income).
                                    </Alert>
                                )}
                                <FormGroup controlId='otherAmountConfirmInitials' style={{ marginBottom: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <ControlLabel style={{ marginBottom: 0 }}>Your initials</ControlLabel>
                                        <OverlayTrigger
                                            trigger={['hover', 'focus']}
                                            placement='top'
                                            overlay={
                                                <Popover id='popover-other-amount-initials' title='Audit trail'>
                                                    Recorded on the audit log with this change so we know who confirmed it.
                                                </Popover>
                                            }
                                        >
                                            <span
                                                role='button'
                                                tabIndex={0}
                                                style={{
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    border: '1px solid #b9c4cf',
                                                    color: '#607182',
                                                    background: '#fff',
                                                    cursor: 'help',
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    lineHeight: '16px',
                                                    textAlign: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                i
                                            </span>
                                        </OverlayTrigger>
                                        <FormControl
                                            type='text'
                                            value={otherAmountConfirmInitials}
                                            onChange={(e: React.FormEvent<FormControl>) =>
                                                setOtherAmountConfirmInitials(
                                                    (e.target as HTMLInputElement).value.slice(0, 3)
                                                )
                                            }
                                            maxLength={3}
                                            autoComplete='off'
                                            disabled={!!savingOtherWeek}
                                            style={{ width: 52, maxWidth: 52, display: 'inline-block' }}
                                        />
                                    </div>
                                </FormGroup>
                                {saveOtherError && <Alert bsStyle='danger' bsSize='sm' style={{ marginBottom: 0 }}>{saveOtherError}</Alert>}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsSize='sm' onClick={() => !savingOtherWeek && setPendingOtherAmount(null)} disabled={!!savingOtherWeek}>
                            Cancel
                        </Button>
                        <Button
                            bsStyle='primary'
                            bsSize='sm'
                            onClick={handleConfirmOtherAmount}
                            disabled={!!savingOtherWeek || !otherAmountInitialsOk}
                        >
                            {savingOtherWeek ? 'Saving…' : 'Confirm'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Panel.Body>
            <Panel.Footer style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button bsSize='sm' onClick={props.onCancel}>Cancel</Button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {hasPendingOtherAmount && (
                            <span
                                role='status'
                                title={`Save Other Amount on week${pendingOtherWeeks.length !== 1 ? 's' : ''} ${pendingOtherWeeks.join(', ')} before recording reimbursement.`}
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#8a6d3b',
                                    background: '#fcf8e3',
                                    border: '1px solid #faebcc',
                                    borderRadius: 4,
                                    padding: '3px 8px',
                                    lineHeight: 1.3
                                }}
                            >
                                Pending Changes
                            </span>
                        )}
                        <Button
                            bsStyle='primary'
                            bsSize='sm'
                            onClick={handleSave}
                            disabled={hasPendingOtherAmount}
                            title={hasPendingOtherAmount
                                ? `Save Other Amount on week${pendingOtherWeeks.length !== 1 ? 's' : ''} ${pendingOtherWeeks.join(', ')} before recording reimbursement.`
                                : undefined}
                        >
                            {buttonLabel}
                        </Button>
                    </div>
                </div>
            </Panel.Footer>
        </Panel>
        </>
    );
}

export default ReimbursementEntry;
